import React, { useEffect } from 'react';
import { Button, Space, Spin, Typography, Select } from 'antd';
import { useParams } from 'react-router-dom';
import { PageTitle } from '@/shared/components/PageTitle';
import { useBreadcrumb } from '@/shared/lib/breadcrumb-context';
import { useAuth } from '@/shared/lib/auth-context';
import { useNotification } from '@/shared/hooks/useNotification';
import { useTranslation } from 'react-i18next';
import { 
  useGetApiAdminGuidesGuideId,
  usePutApiAdminGuidesGuideId,
  GuideStatus,
} from '@/features/guides/api/generated';
import { useGuideFormState } from '../hooks/useGuideFormState';
import { useTabNavigation } from '../hooks/useTabNavigation';
import { GuideForm } from '../components/GuideForm';

export const GuideDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const { setItems } = useBreadcrumb();
  const { contextHolder, notification } = useNotification();
  const { t } = useTranslation();
  const { activeTab, changeTab } = useTabNavigation(id);

  const { data, isLoading, refetch } = useGetApiAdminGuidesGuideId(id || '', {
    query: {
      enabled: !!id && !!token && isAuthenticated && !authLoading,
    },
  });

  const { mutateAsync: saveGuide, isPending: isSaving } = usePutApiAdminGuidesGuideId();
  
  const {
    formState,
    isDirty,
    updateContentField,
    updateRootVehicleWithSelection,
    resetRootVehicle,
    addSolution,
    updateSolution,
    removeSolution,
    reorderSolutions,
    addFilter,
    removeFilterAt,
    updateFilterAt,
    createSavePayload,
    markAsSaved,
    updateStatus,
  } = useGuideFormState(data);

  useEffect(() => {
    // Update breadcrumbs when title or tab changes
    const short = formState?.content.problem ? (formState.content.problem.length > 40 ? `${formState.content.problem.slice(0, 40)}…` : formState.content.problem) : '';
    const tabLabel = activeTab === 'guide' ? t('guides.guide') : activeTab === 'rootVehicle' ? t('guides.sourceVehicle') : activeTab === 'filters' ? t('guides.filters') : t('guides.insights');
    setItems([
      {
        title: (
          <span onClick={() => window.location.assign('/dashboard')} style={{ cursor: 'pointer' }}>
            Home
          </span>
        )
      },
      {
        title: (
          <span onClick={() => window.location.assign('/guides')} style={{ cursor: 'pointer' }}>
            {t('navigation.guides')}
          </span>
        )
      },
      { title: short },
      { title: tabLabel },
    ]);

    return () => setItems(null);
  }, [setItems, formState?.content.problem, activeTab, t]);


  const handleSave = async () => {
    if (!id) return;
    const payload = createSavePayload(id);
    if (!payload) return;

    try {
      await saveGuide({ guideId: id, data: payload });
      notification.success(t('guides.saveSuccessTitle'), t('guides.saveSuccessMessage'));
      markAsSaved();
      await refetch();
    } catch (e) {
      notification.error(t('guides.saveErrorTitle'), t('guides.saveErrorMessage'));
    }
  };

  if (isLoading || !formState) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const { Text, Link } = Typography;
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE') + ' ' + date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  };

  const creatorInfo = data?.guide?.createdBy ? (
    <Space direction="vertical" size={0}>
      <Space>
        <Text strong>Skapad av:</Text>
        <Text>{data.guide.createdBy.name}</Text>
        <Link href={`mailto:${data.guide.createdBy.upn}`}>
          {data.guide.createdBy.upn}
        </Link>
      </Space>
      <Space>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Skapad: {formatDate(data?.guide?.createdAt)}
        </Text>
        {data?.guide?.updatedAt && (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            • Uppdaterad: {formatDate(data.guide.updatedAt)}
          </Text>
        )}
      </Space>
    </Space>
  ) : null;

  const statusOptions = [
    { value: GuideStatus.Draft, label: t('guides.status.draft') },
    { value: GuideStatus.Published, label: t('guides.status.published') },
    { value: GuideStatus.Archived, label: t('guides.status.archived') },
  ];

  return (
    <>
      {contextHolder}
      <div>
        <PageTitle 
          title={(<span style={{ maxWidth: 500, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formState.content.problem}</span>)}
          subtitle={creatorInfo}
        level={4}
        extra={<Space>
          <Select
            value={formState.status}
            onChange={updateStatus}
            options={statusOptions}
            style={{ width: 150 }}
            placeholder={t('guides.status.select')}
          />
          <Button onClick={() => data && data.guide && resetRootVehicle(data.guide)}>{t('guides.resetVehicle')}</Button>
          <Button type="primary" loading={isSaving} onClick={handleSave} disabled={!isDirty || isSaving}>{t('guides.save')}</Button>
        </Space>}
      />
      <GuideForm
        formData={formState}
        onUpdateContent={updateContentField}
        onUpdateRootVehicle={updateRootVehicleWithSelection}
        onAddSolution={addSolution}
        onUpdateSolution={updateSolution}
        onRemoveSolution={removeSolution}
        onReorderSolutions={reorderSolutions}
        onAddFilter={addFilter}
        onRemoveFilter={removeFilterAt}
        onUpdateFilter={updateFilterAt}
        mode="edit"
        activeTab={activeTab}
        onTabChange={changeTab}
      />
      </div>
    </>
  );
};


